// ─────────────────────────────────────────────────────────────────────────────
// Timezone helpers — convert a wall-clock time from one IANA TZ to another and
// build human-friendly TZ labels like "Kolkata (GMT+5:30)".
//
// Used by the Meetings tab: bookings store the COUNSELOR's wall-clock time +
// the GUEST's timezone, so we render the counselor's time in their own TZ and
// also convert it into the guest's TZ for context.
// ─────────────────────────────────────────────────────────────────────────────

/** Minutes that `tz` is ahead of UTC at the given instant (handles DST). */
function tzOffsetMinutes(tz: string, utcMs: number): number {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const p: Record<string, string> = {};
    for (const part of dtf.formatToParts(new Date(utcMs))) {
      if (part.type !== 'literal') p[part.type] = part.value;
    }
    const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return Math.round((asUTC - utcMs) / 60000);
  } catch {
    return 0;
  }
}

/** Interpret `dateStr` (YYYY-MM-DD) + `hhmm` (HH:MM) as wall-clock in `tz`,
 *  return the corresponding UTC epoch ms. */
export function wallClockToUtcMs(dateStr: string, hhmm: string, tz: string): number {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = hhmm.split(':').map(Number);
  const guess = Date.UTC(y, (mo || 1) - 1, d || 1, h || 0, mi || 0);
  // Correct the guess by the TZ offset at that instant. One pass is enough
  // except within the ~1h DST transition window, which booking slots avoid.
  const off = tzOffsetMinutes(tz, guess);
  return guess - off * 60000;
}

/** Break a UTC instant into wall-clock parts in `tz`. */
function partsInTz(utcMs: number, tz: string): { date: string; time: string } {
  try {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
    const p: Record<string, string> = {};
    for (const part of dtf.formatToParts(new Date(utcMs))) {
      if (part.type !== 'literal') p[part.type] = part.value;
    }
    return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
  } catch {
    const dd = new Date(utcMs);
    const pad = (n: number) => String(n).padStart(2, '0');
    return {
      date: `${dd.getUTCFullYear()}-${pad(dd.getUTCMonth() + 1)}-${pad(dd.getUTCDate())}`,
      time: `${pad(dd.getUTCHours())}:${pad(dd.getUTCMinutes())}`,
    };
  }
}

/** Convert a wall-clock date+time from `fromTz` to `toTz`. Returns the target
 *  wall-clock date (YYYY-MM-DD) + time (HH:MM) — the date may shift a day. */
export function convertWallClock(
  dateStr: string,
  hhmm: string,
  fromTz: string,
  toTz: string,
): { date: string; time: string } {
  return partsInTz(wallClockToUtcMs(dateStr, hhmm, fromTz), toTz);
}

/** City segment of an IANA TZ, underscores → spaces. "America/Mexico_City" → "Mexico City". */
export function tzCity(tz: string): string {
  const seg = tz.split('/').pop() ?? tz;
  return seg.replace(/_/g, ' ');
}

/** "GMT+5:30" / "GMT-6" for `tz` at the given instant (DST-aware). */
export function tzOffsetLabel(tz: string, utcMs: number): string {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset', hour: 'numeric' });
    const name = dtf.formatToParts(new Date(utcMs)).find((p) => p.type === 'timeZoneName')?.value;
    if (name) return name; // e.g. "GMT+5:30"
  } catch { /* fall through */ }
  // Fallback: derive from the numeric offset.
  const off = tzOffsetMinutes(tz, utcMs);
  const sign = off >= 0 ? '+' : '-';
  const abs = Math.abs(off);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `GMT${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
}

/** Friendly TZ label: "Kolkata (GMT+5:30)". */
export function tzLabel(tz: string, utcMs: number): string {
  return `${tzCity(tz)} (${tzOffsetLabel(tz, utcMs)})`;
}
